import React, { useEffect, useRef, useState } from "react";
import { API } from "../api";

// Ring and glow per page accent.
const ACCENT_STYLES = {
  green: { track: "stroke-green-100", arc: "stroke-green-600", spin: "stroke-green-400/70", text: "text-green-700", glow: "shadow-green-300/70" },
  amber: { track: "stroke-amber-100", arc: "stroke-amber-600", spin: "stroke-amber-400/70", text: "text-amber-700", glow: "shadow-amber-300/70" },
  red:   { track: "stroke-red-100",   arc: "stroke-red-600",   spin: "stroke-red-400/70",   text: "text-red-700",   glow: "shadow-red-300/70" },
};

// A spinning ring with the job's percentage. The detailed step log stays in the
// backend; only status updates are read here, plus the last error message so a
// failure can still say why (passed to onTerminal as `error`).
export function JobEventLog({ jobId, onTerminal, accent = "green", size = 96 }) {
  const a = ACCENT_STYLES[accent] || ACCENT_STYLES.green;
  const [progress, setProgress] = useState(0);
  const onTerminalRef = useRef(onTerminal);

  useEffect(() => { onTerminalRef.current = onTerminal; }, [onTerminal]);

  useEffect(() => {
    if (!jobId) return undefined;
    setProgress(0);

    const base = API.defaults.baseURL || "";
    const es = new EventSource(`${base}/api/jobs/${jobId}/events`, { withCredentials: true });
    let lastError = "";
    let terminated = false;

    es.onmessage = (ev) => {
      let parsed;
      try { parsed = JSON.parse(ev.data); } catch { return; }
      if (!parsed || typeof parsed !== "object") return;

      if (parsed.level === "error" && parsed.message) lastError = parsed.message;
      if (parsed.level !== "status") return;

      const data = parsed.data || {};
      if (typeof data.progress === "number") setProgress(data.progress);
      if (data.status === "completed" || data.status === "failed") {
        terminated = true;
        es.close();
        if (data.status === "completed") setProgress(100);
        onTerminalRef.current?.({ status: data.status, error: lastError });
      }
    };
    // EventSource reconnects on its own; the replay buffer fills any gap.
    es.onerror = () => { if (terminated) es.close(); };

    return () => { terminated = true; es.close(); };
  }, [jobId]);

  if (!jobId) return null;

  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.min(Math.max(progress, 0), 100) / 100);
  const centre = size / 2;

  return (
    <div
      className={`relative inline-flex items-center justify-center rounded-full bg-white shadow-lg ${a.glow}`}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progress}
    >
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={centre} cy={centre} r={radius} fill="none" strokeWidth={stroke} className={a.track} />
        <circle cx={centre} cy={centre} r={radius} fill="none" strokeWidth={stroke}
                strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset}
                className={`${a.arc} transition-[stroke-dashoffset] duration-500 ease-out`} />
      </svg>
      {/* A short arc circling continuously, so the ring shows life between updates. */}
      <svg width={size} height={size} className="absolute inset-0 animate-spin [animation-duration:1.6s]">
        <circle cx={centre} cy={centre} r={radius} fill="none" strokeWidth={stroke}
                strokeLinecap="round" strokeDasharray={`${circumference * 0.12} ${circumference}`}
                className={a.spin} />
      </svg>
      <span className={`relative text-lg font-black tabular-nums ${a.text}`}>{Math.round(progress)}%</span>
    </div>
  );
}
